import { SingleSelectDropdownObject } from '@/components/PortfolioOptimization/Accordions/OptimizationGoalAndConstraints/SingleSelectDropdownObject'
import { ConfigurableList } from '@/components/Shared/ConfigurableList'
import { SearchBar } from '@/components/Shared/SearchBar'
import {
  DEFAULT_FLEX_CENTER,
  FLEX_ROW_SPACE_BETWEEN,
} from '@/constants/scenarioBuilder.constant'
import { ConstraintOperation } from '@/enums/portfolio-optimization.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IConstraint,
  IConstraintItem,
  IConstraintOption,
} from '@/interfaces/portfolioOptimization.interface'
import {
  getConstraintOptions,
  getConstraintsItemDetails,
} from '@/services/portfolio-optimization/constraints.service'
import { resetSubsetScreening } from '@/states/candidateScreen.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import SubsetCandidateScreenModal from './SubsetCandidateScreenModal'

const Operators = [
  { id: '<=', label: 'Less than or Equal to (<=)', code: 'opLessEq' },
  { id: '==', label: 'Equals (==)', code: 'opEq' },
  { id: '>=', label: 'Greater than or Equal to (>=)', code: 'opGrEq' },
]

type ConstraintModalProps = {
  isOpen: boolean
  mode: ConstraintOperation
  onApplyConstraints: (statements: IConstraint) => void
  onClose: () => void
}

const styles = {
  container: {
    display: 'flex',
    height: '70vh',
    // gap: '16px',
    overflow: 'hidden',
  },
  leftColumn: {
    flex: '0 0 40%',
    minWidth: '30%',
    border: '1px solid #E5E5E5',

    // padding: '16px',
  },
  rightColumn: {
    flex: '1',
    border: '1px solid #E5E5E5',
    // padding: '16px',
  },
  header: {
    display: 'flex',
    backgroundColor: '#F8F8F8',
    padding: '8px 16px',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  list: {
    height: '100%',
    overflow: 'scroll',
  },
  listItem: {
    padding: '8px 16px',
    border: '1px solid red',
  },
}

const AddConstraintGoal = ({
  isOpen,
  mode,
  onApplyConstraints,
  onClose,
}: ConstraintModalProps) => {
  const optimizationType = 'Horizon'
  const appDispatch = useDispatch()
  const [constraintsOptions, setConstraintsOptions] = useState<
    IConstraintOption[]
  >([])
  const [loadingConstraintsOptions, setLoadingConstraintsOptions] =
    useState(false)

  // For Left side screening option list
  const [constraintOption, setConstraintOption] =
    useState<IConstraintOption | null>(null)

  const [selectedConstraintDetails, setSelectedConstraintDetails] =
    useState<IConstraintItem | null>(null)

  // For search bar
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [type, setType] = useState<string>('')
  const [penalty, setPenalty] = useState<number>(3)
  const [ranges, setRanges] = useState<string>('')
  const [issue, setIssue] = useState<string>('STIFSTIF')
  const [operator, setOperator] = useState<string>('')
  const [curveSwap, setCurveSwap] = useState<boolean>(false)
  const [matchBenchmark, setMatchBenchmark] = useState<boolean>(false)
  const [scaleSpread, setScaleSpread] = useState<string>('')
  const [marketValue, setMarketValue] = useState<string | number | undefined>(
    '',
  )
  const [isFormError, setFormError] = useState<boolean>(false)
  const [availableOperators, setAvailableOperators] = useState<
    typeof Operators
  >([])
  const [
    availableTargetStringValidValues,
    setAvailableTargetStringValidValues,
  ] = useState<string[]>([])
  const [subsetModalOpen, setSubsetModalOpen] = useState<boolean>(false)
  const [isSubsetSelected, setIsSubsetSelected] = useState<boolean>(false)
  const handleConfigurableListSelect = useCallback(
    (option: IConstraintOption) => {
      setConstraintOption(option)
    },
    [],
  )
  const candidateScreenState = useAppSelector((s) => s.candidateScreen)
  const handleSearchChange = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm)
  }, [])

  const handleRangeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRanges(event?.target?.value)
      if (event?.target?.value === 'subset') {
        setIsSubsetSelected(true)
      } else {
        setIsSubsetSelected(false)
      }
    },
    [],
  )
  useEffect(() => {
    if (ranges === 'subset') {
      setIsSubsetSelected(true)
    } else {
      setIsSubsetSelected(false)
    }
  }, [ranges])

  const handleTypeChanges = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setType(event?.target?.value)
    },
    [],
  )

  const handleScaleSpreadChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setScaleSpread(event?.target?.value)
    },
    [],
  )

  const handleApplyGoalConstraintClick = useCallback(() => {
    if (!selectedConstraintDetails) {
      setFormError(true)
      appDispatch(
        showSnackbar({
          message: 'A constraint must be selected',
          severity: 'error',
        }),
      )
      return
    }

    if (!selectedConstraintDetails?.description) {
      setFormError(true)
      appDispatch(
        showSnackbar({
          message: 'Selected Constraint must have a description',
          severity: 'error',
        }),
      )
      return
    }

    if (availableOperators?.length > 0 && !operator) {
      setFormError(true)
      appDispatch(
        showSnackbar({
          message: 'An operator must be selected',
          severity: 'error',
        }),
      )
      return
    }

    if (
      selectedConstraintDetails.availableOptions.target &&
      (!marketValue ||
        marketValue.toString().trim() === '-' ||
        marketValue.toString().trim() === '.')
    ) {
      setFormError(true)
      appDispatch(
        showSnackbar({
          message: 'A market numeric value must be provided',
          severity: 'error',
        }),
      )
      return
    }
    if (
      selectedConstraintDetails.availableOptions.targetString &&
      (!marketValue || marketValue.toString().trim().length === 0)
    ) {
      setFormError(true)
      appDispatch(
        showSnackbar({
          message: 'A market string value must be provided',
          severity: 'error',
        }),
      )
      return
    }

    let requestBody: IConstraint = {
      id: selectedConstraintDetails.id,
      description: selectedConstraintDetails.description,
      type,
      ranges,
      penalty,
      operator,
    }
    if (selectedConstraintDetails.availableOptions.target) {
      requestBody.target = Number(marketValue)
    }
    if (selectedConstraintDetails.availableOptions.targetString) {
      requestBody.targetString = marketValue
    }
    if (ranges === 'issue') {
      requestBody.issuer = issue
    }
    if (ranges === 'subset') {
      requestBody.subset = candidateScreenState.subsetScreeningCriteriaList
    }
    if (curveSwap) {
      requestBody.useSwapCurve = true
    }
    if (matchBenchmark) {
      requestBody.matchBenchmarkValue = true
    }
    if (scaleSpread?.length) {
      requestBody.matchBenchmarkScaleSpread = scaleSpread
    }
    onApplyConstraints(requestBody)
  }, [
    selectedConstraintDetails,
    operator,
    marketValue,
    type,
    ranges,
    penalty,
    onApplyConstraints,
  ])

  const handleClose = useCallback(
    (event: any, reason: string) => {
      // Prevent closing the modal when backdrop/outside modal is clicked
      if (reason && reason === 'backdropClick') return
      onClose()
    },
    [onClose],
  )

  const ConfigurableListMemo = useMemo(() => {
    return React.memo(ConfigurableList)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      setLoadingConstraintsOptions(true)
      try {
        const response = await getConstraintOptions()
        setConstraintsOptions(response)
        setConstraintOption(response[0])
      } catch (err) {
        appDispatch(
          showSnackbar({
            message: `Error fetching Constraint Options ${err instanceof Error ? err.message : String(err)}`,
            severity: 'error',
          }),
        )
        setConstraintsOptions([])
      } finally {
        setLoadingConstraintsOptions(false)
      }
    }
    fetchData()
  }, [])

  const fetchConstraintDetails = async (
    constraintOption: IConstraintOption,
    optimizationType: string,
  ) => {
    try {
      setMarketValue('')
      const response = await getConstraintsItemDetails(
        constraintOption.id,
        optimizationType,
      )
      setSelectedConstraintDetails(response)
    } catch (err) {
      appDispatch(
        showSnackbar({
          message: `Error fetching Constraint Details ${err instanceof Error ? err.message : String(err)}`,
          severity: 'error',
        }),
      )
    }
  }

  useEffect(() => {
    if (constraintOption) {
      fetchConstraintDetails(constraintOption, optimizationType)
    }
  }, [constraintOption])

  useEffect(() => {
    if (selectedConstraintDetails) {
      const availableOperators = Operators.filter(
        (operator) =>
          selectedConstraintDetails?.availableOptions?.[
            operator.code as keyof typeof selectedConstraintDetails.availableOptions
          ],
      )
      setAvailableOperators(availableOperators)
      setFormError(false)

      const { targetStringValidValues } =
        selectedConstraintDetails?.availableOptions
      if (targetStringValidValues) {
        setAvailableTargetStringValidValues(targetStringValidValues)
      }
    }
  }, [selectedConstraintDetails])

  useEffect(() => {
    if (selectedConstraintDetails) {
      const { defaultSettings } = selectedConstraintDetails
      const {
        goal,
        constraint,
        global,
        subset,
        issue,
        scale,
        spread,
        matchBenchmarkValue,
        useSwapCurve,
        opLessEq,
        opEq,
        opGrEq,
      } = defaultSettings

      // to get the prefill type
      if (goal) {
        setType('goal')
      } else if (constraint) {
        setType('constraint')
      } else {
        setType('')
      }

      // to get the prefill ranges
      if (global) {
        setRanges('global')
      } else if (subset) {
        setRanges('subset')
      } else if (issue) {
        setRanges('issue')
      } else {
        setRanges('')
      }

      // to get the prefill scale and spread
      if (scale) {
        setScaleSpread('scale')
      } else if (spread) {
        setScaleSpread('spread')
      } else {
        setScaleSpread('')
      }

      // to get the prefill benchmark
      setMatchBenchmark(!!matchBenchmarkValue)

      // to get the pre check curve swap
      setCurveSwap(!!useSwapCurve)

      // to get the prefill value operator
      if (opLessEq) {
        setOperator(Operators.find((op) => op.code === 'opLessEq')?.id || '')
      } else if (opEq) {
        setOperator(Operators.find((op) => op.code === 'opEq')?.id || '')
      } else if (opGrEq) {
        setOperator(Operators.find((op) => op.code === 'opGrEq')?.id || '')
      } else {
        setOperator('')
      }
    }
  }, [selectedConstraintDetails])

  const handleMarketValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (selectedConstraintDetails?.availableOptions?.target) {
      // Accept decimals and negative numbers
      const regex = /^-?\d*(\.\d{0,3})?$/

      // Check value matches pattern and is within range
      if (value === '' || regex.test(value)) {
        // Allow "-" or trailing "." as intermediate input
        if (value === '-' || value.endsWith('.')) {
          setMarketValue(value)
        } else {
          const num = Number(value)
          if (
            !isNaN(num) &&
            num >= -999999999999.999 &&
            num <= 999999999999.999
          ) {
            setMarketValue(value)
          } else {
            // Still allow the value while typing
            setMarketValue(value)
          }
        }
      }
    } else if (selectedConstraintDetails?.availableOptions?.targetString) {
      // Alphanumeric only, max 4 characters
      if (/^[a-zA-Z0-9]{0,4}$/.test(value)) {
        setMarketValue(value)
      }
    }
  }

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth='md'>
        <DialogTitle>Setup or Edit Constraint/Goal</DialogTitle>
        <Divider />
        <DialogContent>
          <Paper style={styles.container}>
            {/* Left Column Content */}
            <Box style={styles.leftColumn}>
              <Box style={styles.header}>
                <Typography variant='h5'>Available Items</Typography>
                <SearchBar onChange={handleSearchChange} />
              </Box>
              <Divider />
              <ConfigurableListMemo
                loading={loadingConstraintsOptions}
                options={constraintsOptions as IConstraintOption[]}
                onSelect={handleConfigurableListSelect}
                option={constraintOption || undefined}
                searchTerm={searchTerm}
                displayId={false}
              />
            </Box>
            {/* Right Column Content */}
            <Box style={styles.rightColumn}>
              <Box style={styles.header}>
                <Box sx={{ ...DEFAULT_FLEX_CENTER, gap: 2, height: '40px' }}>
                  <Typography variant='h5'>Properties</Typography>
                </Box>
              </Box>
              <Divider />
              {selectedConstraintDetails && (
                <Box sx={{ overflow: 'auto', height: 'calc(100% - 55px)' }}>
                  <Box padding={1}>
                    <Box
                      sx={{ ...FLEX_ROW_SPACE_BETWEEN, gap: 2, height: '40px' }}
                    >
                      <Typography variant='h5'>Type</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                      }}
                    >
                      <RadioGroup value={type} onChange={handleTypeChanges}>
                        {['constraint', 'goal'].map((option) => (
                          <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio />}
                            label={option}
                            disabled={
                              !selectedConstraintDetails?.availableOptions?.[
                                option as keyof typeof selectedConstraintDetails.availableOptions
                              ]
                            }
                          />
                        ))}
                      </RadioGroup>
                      <TextField
                        variant='outlined'
                        size='small'
                        label='Enter Penalty'
                        defaultValue={Number(penalty) || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setPenalty(Number(e.target.value))
                        }
                        type='number'
                        disabled={
                          !selectedConstraintDetails?.availableOptions?.penalty
                        }
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <Box padding={1}>
                    <Box
                      sx={{ ...FLEX_ROW_SPACE_BETWEEN, gap: 2, height: '40px' }}
                    >
                      <Typography variant='h5'>Ranges</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                      }}
                    >
                      <RadioGroup value={ranges} onChange={handleRangeChange}>
                        {['global', 'subset', 'issue'].map((option) => (
                          <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio />}
                            label={option}
                            disabled={
                              !selectedConstraintDetails?.availableOptions?.[
                                option as keyof typeof selectedConstraintDetails.availableOptions
                              ]
                            }
                          />
                        ))}
                      </RadioGroup>

                      <Box display='flex' gap={3} flexDirection='column'>
                        {ranges === 'subset' && (
                          <Button
                            variant='outlined'
                            onClick={() => {
                              setSubsetModalOpen(true)
                              appDispatch(resetSubsetScreening())
                            }}
                          >
                            Define Subset
                          </Button>
                        )}
                        {ranges === 'issue' && (
                          <TextField
                            variant='outlined'
                            size='small'
                            label='Issue'
                            value={issue}
                            onChange={(e: {
                              target: { value: React.SetStateAction<string> }
                            }) => setIssue(e.target.value)}
                            type='text'
                            disabled={
                              !selectedConstraintDetails?.availableOptions
                                ?.issue
                            }
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                  <Box padding={1}>
                    <Typography variant='h5'>Operator</Typography>
                    <SingleSelectDropdownObject
                      label='Operator'
                      options={availableOperators}
                      onChange={(value: any) => setOperator(value.id)}
                      defaultValue={operator}
                      error={
                        availableOperators?.length > 0 &&
                        !operator &&
                        isFormError
                      }
                      styles={{ margin: 1, minWidth: 300 }}
                      disabled={availableOperators?.length === 0}
                    />
                  </Box>
                  <Divider />
                  {selectedConstraintDetails?.availableOptions?.targetString &&
                    availableTargetStringValidValues.length > 0 && (
                      <Box padding={1}>
                        <Typography variant='h5'>Value</Typography>
                        <SingleSelectDropdownObject
                          label='Value From List'
                          options={availableTargetStringValidValues.map(
                            (value) => ({ id: value, label: value }),
                          )}
                          onChange={(value: any) => setMarketValue(value.id)}
                          defaultValue={marketValue?.toString() || ''}
                          error={
                            selectedConstraintDetails?.availableOptions
                              ?.targetString &&
                            !marketValue &&
                            isFormError
                          }
                          styles={{ margin: 1, minWidth: 300 }}
                          disabled={false}
                        />
                      </Box>
                    )}
                  {selectedConstraintDetails?.availableOptions?.target && (
                    <Box padding={1}>
                      <Typography variant='h5'>Value</Typography>
                      <TextField
                        variant='outlined'
                        size='small'
                        label='Value'
                        value={marketValue}
                        onChange={handleMarketValueChange}
                        error={
                          selectedConstraintDetails?.availableOptions?.target &&
                          !marketValue &&
                          isFormError
                        }
                        type='text'
                        sx={{ margin: 1, minWidth: 300 }}
                        inputProps={{
                          maxLength: 17,
                        }}
                        disabled={false}
                      />
                    </Box>
                  )}
                  <Divider />
                  <Box padding={2}>
                    <FormControlLabel
                      label='Use Curve Swap Value'
                      control={
                        <Checkbox
                          checked={curveSwap}
                          onChange={(event: any) =>
                            setCurveSwap(event?.target?.checked)
                          }
                          inputProps={{ 'aria-label': 'controlled' }}
                          disabled={
                            !selectedConstraintDetails?.availableOptions
                              ?.useSwapCurve
                          }
                        />
                      }
                    />
                  </Box>
                  <Divider />
                  <Box padding={1} paddingBottom={3}>
                    <Typography variant='h5'>Match Benchmark</Typography>
                    <FormControlLabel
                      label='Match Benchmark Value'
                      sx={{ marginLeft: 0 }}
                      control={
                        <Checkbox
                          checked={matchBenchmark}
                          onChange={(event: any) =>
                            setMatchBenchmark(event?.target?.checked)
                          }
                          inputProps={{ 'aria-label': 'controlled' }}
                          disabled={
                            !selectedConstraintDetails?.availableOptions
                              ?.matchBenchmarkValue
                          }
                        />
                      }
                    />
                    <br />
                    <RadioGroup
                      value={scaleSpread}
                      onChange={handleScaleSpreadChange}
                    >
                      {['scale', 'spread'].map((option) => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          control={<Radio />}
                          label={option}
                          disabled={
                            !selectedConstraintDetails?.availableOptions?.[
                              option as keyof typeof selectedConstraintDetails.availableOptions
                            ]
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApplyGoalConstraintClick} variant='contained'>
            {mode === ConstraintOperation.EDIT
              ? ConstraintOperation.EDIT
              : ConstraintOperation.ADD}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <SubsetCandidateScreenModal
        isOpen={subsetModalOpen}
        onClose={() => setSubsetModalOpen(false)}
        isSubset={isSubsetSelected}
      />
    </>
  )
}

export default AddConstraintGoal
